import React from "react";
import PropTypes from "prop-types";
import helpers from "../../../../common/utils/utils";
import Expander from "../../../../components/Expander";
import PublicationLinks from "../../../../components/Publications/components/PublicationLinks";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import HelperText from "../../../../components/atoms/HelperText";
import RenderBullet from "../../../../components/atoms/RenderBullet/RenderBullet";
import { approxAmber } from "../../../../common/helpers/colors";

const COLOR_SORT = {
  Red: 0,
  Amber: 1,
  Green: 2,
};

const TOOLTIPS = {
  Red: "Low Evidence",
  Amber: "Moderate Evidence",
  Green: "High Evidence",
};

const cleanModeOfPathogenicity = (m) => {
  return m
    .replace("(as defined in pop up message) ", "")
    .replace(/ -\s?please provide details in the comments/g, "");
};

function PanelAppTable({ items }) {
  return (
    <table className="varsome-table" style={{ wordWrap: "break-word" }}>
      <thead>
        <tr>
          <th width="20%">
            Name
            <br />
            Description
          </th>
          <th width="15%" className="tw-text-center">
            Phenotypes
          </th>
          <th width="10%" className="tw-text-center">
            Level of confidence
          </th>
          <th width="15%" className="tw-text-center">
            Evidence
          </th>
          <th width="10%" className="tw-text-center">
            Penetrance
          </th>
          <th width="10%" className="tw-text-center">
            Mode of inheritance
          </th>
          <th width="10%" className="tw-text-center">
            Mode of pathogenicity
          </th>
          <th width="10%" className="tw-text-center">
            Publications
          </th>
        </tr>
      </thead>
      <Expander showMore="10" component="tbody">
        {items
          .sort((a, b) => {
            // Assigning numbers to colors in order to sort
            const ac = COLOR_SORT[a.genecolorreview];
            const bc = COLOR_SORT[b.genecolorreview];
            return bc - ac;
          })
          .map((item, i) => {
            let v;
            let relevantDisorders;
            if (item.genelistotherdata) {
              v = item.genelistotherdata.currentversion;
              relevantDisorders = item.genelistotherdata.relevant_disorders;
            }
            return (
              <tr key={i}>
                <td>
                  {item.genecolorreview && (
                    <div
                      className="tw-inline-block"
                      style={{
                        width: "1.2rem",
                        height: "1.2rem",
                        verticalAlign: "middle",
                        marginRight: "0.3rem",
                        backgroundColor:
                          item.genecolorreview === "Amber"
                            ? approxAmber
                            : item.genecolorreview.toLowerCase(),
                      }}
                      data-tooltip={
                        TOOLTIPS[item.genecolorreview] || "Unknown Evidence"
                      }
                    />
                  )}
                  {item.genelistname && (
                    <span>
                      <strong>
                        <a
                          href={getVarsoUrl(
                            `https://panelapp.genomicsengland.co.uk/panels/${item.panelid}`
                          )}
                          target="_blank"
                        >
                          {item.genelistname.split(".")[0].replace(/_/g, " ")}
                        </a>
                      </strong>{" "}
                      {v && <small>{`(Version: ${v})`}</small>}
                      <br />
                    </span>
                  )}
                  {item.genelistdescription || "-"}
                  {Array.isArray(relevantDisorders) &&
                    relevantDisorders.length > 0 && (
                      <div>
                        <HelperText
                          RenderContent={() => (
                            <div>
                              <h4>Relevant disorders</h4>
                              <div className="tw-mt-4">
                                {item.genelistotherdata.relevant_disorders.map(
                                  (d) => (
                                    <div key={d.name}>
                                      &#9702;
                                      {d.name}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                          anchor={<a>Relevant disorders</a>}
                        />
                      </div>
                    )}
                </td>
                <td className="varsome-text-center">
                  {Array.isArray(item.phenotypes) &&
                    item.phenotypes.map((phen, k) => (
                      <div key={k}>
                        {item.phenotypes.length > 1 && <RenderBullet />}
                        {phen}
                      </div>
                    ))}
                  {(!Array.isArray(item.phenotypes) ||
                    item.phenotypes.length === 0) &&
                    "-"}
                </td>
                <td className="varsome-text-center">
                  {item.levelofconfidence.split("Evidence")[0] || "-"}
                </td>
                <td className="varsome-text-center">
                  {Array.isArray(item.evidences) &&
                    item.evidences.map((ev, j) => (
                      <div key={j}>
                        {item.evidences.length > 1 && <RenderBullet />}
                        {ev}
                      </div>
                    ))}
                  {(!Array.isArray(item.evidences) ||
                    item.evidences.length === 0) &&
                    "-"}
                </td>
                <td className="varsome-text-center">
                  {item.penetrance || "-"}
                </td>
                <td className="varsome-text-center">
                  {item.modeofinheritance
                    ? helpers.capitalizeFirstLetter(
                        item.modeofinheritance.split("_").join(" ")
                      )
                    : "-"}
                </td>
                <td className="varsome-text-center">
                  {item.modeofpathogenicity
                    ? cleanModeOfPathogenicity(item.modeofpathogenicity)
                    : "-"}
                </td>
                <td className="varsome-text-center">
                  {item.pub_med_references && (
                    <PublicationLinks
                      limit={10}
                      ids={item.pub_med_references}
                    />
                  )}
                </td>
              </tr>
            );
          })}
      </Expander>
    </table>
  );
}

PanelAppTable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      evidences: PropTypes.arrayOf(PropTypes.string),
      genecolorreview: PropTypes.string,
      genelistdescription: PropTypes.string,
      genelistname: PropTypes.string,
      genelistotherdata: PropTypes.shape({
        currentversion: PropTypes.string,
        relevant_disorders: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            id: PropTypes.number,
          })
        ),
        types: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            id: PropTypes.number,
          })
        ),
      }),
      levelofconfidence: PropTypes.string,
      modeofinheritance: PropTypes.string,
      modeofpathogenicity: PropTypes.string,
      panelid: PropTypes.string,
      penetrance: PropTypes.string,
      phenotypes: PropTypes.arrayOf(PropTypes.string),
      pub_med_references: PropTypes.arrayOf(PropTypes.number),
    })
  ),
};

PanelAppTable.defaultProps = {
  items: null,
};
export default PanelAppTable;
